/**
 * Champ de recherche d'un sous-poste (combobox ARIA, « liste » en saisie
 * semi-automatique) : on tape quelques lettres, la liste se réduit aux comptes
 * qui correspondent, on choisit à la souris ou au clavier. Remplace une liste
 * déroulante de plus d'une centaine de comptes, impraticable sans filtre.
 *
 * Chaque champ ne crée que son `<input>` et une `<ul>` vide : les options sont
 * construites à l'ouverture (une seule liste est ouverte à la fois) et retirées
 * à la fermeture, car l'écran en porte plusieurs centaines.
 */

import { h, vider } from '../../dom.js';
import { libellePosteOuHorsBudget } from '../../postes.js';
import type { SousPoste } from './api.js';
import { libelleCompte } from './comptes.js';
import { filtrerSousPostes, limiterEtGrouper } from './filtre-sous-postes.js';

export interface OptionsRecherche {
  /** Sous-postes proposés : déjà filtrés par sens, sans les archivés. */
  readonly choix: readonly SousPoste[];
  /** Appelé avec l'identifiant du sous-poste choisi. */
  readonly surChoix: (id: string) => void;
  /** Nom accessible du champ. */
  readonly libelle: string;
}

export interface ChampRechercheSousPoste {
  /** À insérer dans le document. */
  readonly element: HTMLElement;
  /** Le champ de saisie : désactivable, focalisable. */
  readonly champ: HTMLInputElement;
  /** Efface la saisie et le choix affiché (après un refus, par exemple). */
  readonly reinitialiser: () => void;
}

const PLACEHOLDER = 'Classer dans… (tapez pour filtrer)';
const LARGEUR_MIN_LISTE = 384;
const HAUTEUR_MAX_LISTE = 320;
const MARGE = 8;

let compteur = 0;

export function champRechercheSousPoste(options: OptionsRecherche): ChampRechercheSousPoste {
  const uid = `recherche-sous-poste-${String(++compteur)}`;
  const idListe = `${uid}-liste`;

  const champ = h('input', {
    type: 'text',
    role: 'combobox',
    classe: 'recherche-champ',
    autocomplete: 'off',
    spellcheck: 'false',
    placeholder: PLACEHOLDER,
    'aria-label': options.libelle,
    'aria-autocomplete': 'list',
    'aria-expanded': 'false',
    'aria-controls': idListe,
  });
  const liste = h('ul', { id: idListe, role: 'listbox', classe: 'recherche-liste', 'aria-label': options.libelle, hidden: true });
  const element = h('span', { classe: 'recherche-sous-poste' }, champ, liste);

  /** Options affichées, dans l'ordre de la liste (les en-têtes de poste n'en font pas partie). */
  let affichees: { readonly el: HTMLElement; readonly sousPoste: SousPoste }[] = [];
  let actif = -1;
  let ouverte = false;
  /** Un sous-poste est choisi et son libellé occupe le champ. */
  let choisi = false;

  const activer = (i: number): void => {
    affichees[actif]?.el.setAttribute('aria-selected', 'false');
    affichees[actif]?.el.classList.remove('actif');
    actif = i;
    const cible = affichees[i];
    if (!cible) {
      champ.removeAttribute('aria-activedescendant');
      return;
    }
    cible.el.setAttribute('aria-selected', 'true');
    cible.el.classList.add('actif');
    champ.setAttribute('aria-activedescendant', cible.el.id);
    cible.el.scrollIntoView({ block: 'nearest' });
  };

  const dessiner = (): void => {
    const resultats = filtrerSousPostes(options.choix, choisi ? '' : champ.value, libellePosteOuHorsBudget);
    const { groupes, restants } = limiterEtGrouper(resultats);
    vider(liste);
    affichees = [];
    actif = -1;
    champ.removeAttribute('aria-activedescendant');
    if (resultats.length === 0) {
      liste.appendChild(h('li', { role: 'presentation', classe: 'recherche-vide' }, 'Aucun compte ne correspond'));
      return;
    }
    for (const [poste, membres] of groupes) {
      liste.appendChild(h('li', { role: 'presentation', classe: 'recherche-groupe' }, libellePosteOuHorsBudget(poste)));
      for (const sousPoste of membres) {
        const el = h(
          'li',
          { role: 'option', id: `${uid}-option-${String(affichees.length)}`, classe: 'recherche-option', 'aria-selected': 'false' },
          libelleCompte(sousPoste),
        );
        affichees.push({ el, sousPoste });
        liste.appendChild(el);
      }
    }
    if (restants > 0) {
      const texte = restants === 1 ? '1 autre résultat' : `${String(restants)} autres résultats`;
      liste.appendChild(h('li', { role: 'presentation', classe: 'recherche-suite' }, `${texte} — précisez la saisie`));
    }
  };

  /**
   * La liste est en position fixe : le tableau qui la porte rogne ses débordements.
   * Elle s'ouvre sous le champ, ou au-dessus quand il manque de la place en bas.
   */
  const positionner = (): void => {
    const r = champ.getBoundingClientRect();
    const largeur = Math.min(Math.max(r.width, LARGEUR_MIN_LISTE), window.innerWidth - 2 * MARGE);
    const gauche = Math.max(MARGE, Math.min(r.left, window.innerWidth - largeur - MARGE));
    const dessous = window.innerHeight - r.bottom - MARGE;
    const dessus = r.top - MARGE;
    const enHaut = dessous < 200 && dessus > dessous;
    liste.style.left = `${String(gauche)}px`;
    liste.style.width = `${String(largeur)}px`;
    liste.style.maxHeight = `${String(Math.max(120, Math.min(enHaut ? dessus : dessous, HAUTEUR_MAX_LISTE)))}px`;
    liste.style.top = enHaut ? 'auto' : `${String(r.bottom + 2)}px`;
    liste.style.bottom = enHaut ? `${String(window.innerHeight - r.top + 2)}px` : 'auto';
  };

  const surDefilement = (ev: Event): void => {
    if (ev.target instanceof Node && liste.contains(ev.target)) return;
    positionner();
  };

  const ouvrir = (): void => {
    if (champ.disabled) return;
    dessiner();
    if (!ouverte) {
      ouverte = true;
      liste.hidden = false;
      champ.setAttribute('aria-expanded', 'true');
      window.addEventListener('scroll', surDefilement, true);
      window.addEventListener('resize', positionner);
    }
    positionner();
  };

  const fermer = (): void => {
    if (!ouverte) return;
    ouverte = false;
    liste.hidden = true;
    vider(liste);
    affichees = [];
    actif = -1;
    champ.removeAttribute('aria-activedescendant');
    champ.setAttribute('aria-expanded', 'false');
    window.removeEventListener('scroll', surDefilement, true);
    window.removeEventListener('resize', positionner);
  };

  const choisir = (sousPoste: SousPoste): void => {
    choisi = true;
    champ.value = libelleCompte(sousPoste);
    fermer();
    options.surChoix(sousPoste.id);
  };

  const reinitialiser = (): void => {
    choisi = false;
    champ.value = '';
    fermer();
  };

  const deplacer = (pas: 1 | -1): void => {
    const n = affichees.length;
    if (n === 0) return;
    activer(actif === -1 ? (pas === 1 ? 0 : n - 1) : (actif + pas + n) % n);
  };

  champ.addEventListener('focus', () => {
    ouvrir();
    if (choisi) champ.select();
  });
  champ.addEventListener('click', () => {
    if (!ouverte) ouvrir();
  });
  champ.addEventListener('input', () => {
    choisi = false;
    ouvrir();
  });
  champ.addEventListener('blur', () => {
    fermer();
    // Une saisie sans choix ne désigne aucun compte : on ne la laisse pas passer pour une sélection.
    if (!choisi) champ.value = '';
  });
  champ.addEventListener('keydown', (ev) => {
    if (ev.isComposing) return;
    switch (ev.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        ev.preventDefault();
        if (!ouverte) ouvrir();
        deplacer(ev.key === 'ArrowDown' ? 1 : -1);
        break;
      case 'Enter': {
        if (!ouverte) break;
        const cible = affichees[actif] ?? (affichees.length === 1 ? affichees[0] : undefined);
        if (!cible) break;
        ev.preventDefault();
        choisir(cible.sousPoste);
        break;
      }
      case 'Escape':
        if (ouverte) {
          ev.preventDefault();
          ev.stopPropagation();
          fermer();
        } else if (champ.value !== '') {
          ev.preventDefault();
          reinitialiser();
        }
        break;
      default:
        break;
    }
  });

  // `mousedown` et non `click` : le champ perdrait le focus (donc la liste) avant le clic.
  liste.addEventListener('mousedown', (ev) => {
    ev.preventDefault();
    const li = ev.target instanceof Element ? ev.target.closest('li[role="option"]') : null;
    const cible = affichees.find((a) => a.el === li);
    if (cible) choisir(cible.sousPoste);
  });
  liste.addEventListener('mousemove', (ev) => {
    const li = ev.target instanceof Element ? ev.target.closest('li[role="option"]') : null;
    const i = affichees.findIndex((a) => a.el === li);
    if (i !== -1 && i !== actif) activer(i);
  });

  return { element, champ, reinitialiser };
}
